import { useEffect, useState, useCallback } from "react";
import * as Yup from "yup";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
    arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ApiService from "../../services/apiService";
import Loader from "../../elements/Loader";

const EMPTY_FORM = { title: "", imageUrl: "", deepLink: "", isActive: true };

const validationSchema = Yup.object().shape({
    title: Yup.string().nullable(),
    imageUrl: Yup.string().url("Must be a valid URL").required("Image is required"),
    deepLink: Yup.string().required("Deep link is required"),
    isActive: Yup.boolean(),
});

function ImageDropzone({ onUpload, uploading, currentImageUrl }) {
    const onDrop = useCallback((files) => onUpload(files), [onUpload]);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [".jpg", ".jpeg", ".png", ".webp"] },
        maxFiles: 1,
    });

    return (
        <div className="mb-3">
            <label className="form-label">Banner Image</label>
            <div
                {...getRootProps()}
                style={{
                    border: "2px dashed #dee2e6",
                    borderRadius: "5px",
                    padding: "20px",
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragActive ? "#f8f9fa" : "white",
                }}
            >
                <input {...getInputProps()} />
                {uploading ? <p className="mb-0">Uploading...</p>
                    : isDragActive ? <p className="mb-0">Drop here...</p>
                    : <p className="mb-0 text-muted">Drag & drop or click to upload</p>}
            </div>
            {currentImageUrl && (
                <img src={currentImageUrl} alt="Preview" className="mt-2" style={{ maxHeight: "80px", borderRadius: "4px" }} />
            )}
        </div>
    );
}

function SortableRow({ banner, index, onEdit, onToggle, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: banner.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        backgroundColor: isDragging ? "#f8f9fa" : undefined,
    };

    return (
        <tr ref={setNodeRef} style={style}>
            <td {...attributes} {...listeners} style={{ cursor: "grab", width: "32px" }}>
                <span style={{ fontSize: "18px", color: "#aaa", userSelect: "none" }}>⠿</span>
            </td>
            <td><strong>{index + 1}</strong></td>
            <td>
                <img src={banner.imageUrl} alt={banner.title || "Banner"} style={{ height: "50px", borderRadius: "4px", objectFit: "cover" }} />
            </td>
            <td>{banner.title || "—"}</td>
            <td><span style={{ fontSize: "12px", wordBreak: "break-all" }}>{banner.deepLink}</span></td>
            <td>
                <span className={`badge badge-${banner.isActive ? "success" : "danger"}`}>
                    {banner.isActive ? "Active" : "Inactive"}
                </span>
            </td>
            <td>
                <div className="d-flex gap-2">
                    <button className="btn btn-primary btn-sm" onClick={() => onEdit(banner)}>Edit</button>
                    <button className={`btn btn-sm btn-${banner.isActive ? "warning" : "success"}`} onClick={() => onToggle(banner)}>
                        {banner.isActive ? "Deactivate" : "Activate"}
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => onDelete(banner.id)}>Delete</button>
                </div>
            </td>
        </tr>
    );
}

export default function BannerList() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [currentImageUrl, setCurrentImageUrl] = useState("");

    const sensors = useSensors(useSensor(PointerSensor));

    useEffect(() => { fetchBanners(); }, []);

    const fetchBanners = async () => {
        setLoading(true);
        try {
            const res = await ApiService.get("banner/all");
            setBanners(res.data);
        } catch {
            toast.error("Failed to fetch banners");
        } finally {
            setLoading(false);
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = banners.findIndex((b) => b.id === active.id);
        const newIndex = banners.findIndex((b) => b.id === over.id);
        const reordered = arrayMove(banners, oldIndex, newIndex);
        setBanners(reordered);

        try {
            await ApiService.patch("banner/reorder", { order: reordered.map((b) => b.id) });
        } catch {
            toast.error("Failed to save order");
            fetchBanners(); // revert on failure
        }
    };

    const openAdd = () => { setEditing(null); setCurrentImageUrl(""); setModalOpen(true); };
    const openEdit = (banner) => { setEditing(banner); setCurrentImageUrl(banner.imageUrl || ""); setModalOpen(true); };
    const closeModal = () => setModalOpen(false);

    const handleImageUpload = async (files, setFieldValue) => {
        const file = files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await ApiService.post("upload/banner", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = res?.data?.url || res?.url;
            if (url) {
                setFieldValue("imageUrl", url);
                setCurrentImageUrl(url);
                toast.success("Image uploaded");
            }
        } catch {
            toast.error("Image upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            if (editing) {
                await ApiService.put(`banner/${editing.id}`, values);
                toast.success("Banner updated");
            } else {
                await ApiService.post("banner", values);
                toast.success("Banner created");
            }
            closeModal();
            fetchBanners();
        } catch {
            toast.error("Failed to save banner");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this banner?")) return;
        try {
            await ApiService.delete(`banner/${id}`);
            toast.success("Banner deleted");
            fetchBanners();
        } catch {
            toast.error("Failed to delete banner");
        }
    };

    const handleToggleActive = async (banner) => {
        try {
            await ApiService.put(`banner/${banner.id}`, { isActive: !banner.isActive });
            toast.success(`Banner ${banner.isActive ? "deactivated" : "activated"}`);
            fetchBanners();
        } catch {
            toast.error("Failed to update banner");
        }
    };

    const initialValues = editing
        ? { title: editing.title || "", imageUrl: editing.imageUrl || "", deepLink: editing.deepLink || "", isActive: editing.isActive ?? true }
        : EMPTY_FORM;

    return (
        <div className="row">
            <div className="col-xl-12">
                {loading && <Loader />}
                <div className="card dz-card">
                    <div className="card-header flex-wrap d-flex justify-content-between border-0">
                        <div>
                            <h4 className="card-title">Hero Banner Images</h4>
                            <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>Drag rows to reorder</p>
                        </div>
                        <button className="btn btn-primary" onClick={openAdd}>Add Banner</button>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-responsive-md">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th><strong>#</strong></th>
                                        <th><strong>Preview</strong></th>
                                        <th><strong>Title</strong></th>
                                        <th><strong>Deep Link</strong></th>
                                        <th><strong>Status</strong></th>
                                        <th><strong>Actions</strong></th>
                                    </tr>
                                </thead>
                                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                    <SortableContext items={banners.map((b) => b.id)} strategy={verticalListSortingStrategy}>
                                        <tbody>
                                            {banners.map((banner, index) => (
                                                <SortableRow
                                                    key={banner.id}
                                                    banner={banner}
                                                    index={index}
                                                    onEdit={openEdit}
                                                    onToggle={handleToggleActive}
                                                    onDelete={handleDelete}
                                                />
                                            ))}
                                            {!loading && banners.length === 0 && (
                                                <tr><td colSpan="7" className="text-center text-muted py-4">No banners yet. Click "Add Banner" to create one.</td></tr>
                                            )}
                                        </tbody>
                                    </SortableContext>
                                </DndContext>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {modalOpen && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{editing ? "Edit Banner" : "Add Banner"}</h5>
                                <button type="button" className="btn-close" onClick={closeModal} />
                            </div>
                            <Formik
                                initialValues={initialValues}
                                enableReinitialize
                                validationSchema={validationSchema}
                                onSubmit={handleSubmit}
                            >
                                {({ setFieldValue, values }) => (
                                    <Form>
                                        <div className="modal-body">
                                            <ImageDropzone
                                                onUpload={(files) => handleImageUpload(files, setFieldValue)}
                                                uploading={uploading}
                                                currentImageUrl={currentImageUrl}
                                            />
                                            <ErrorMessage name="imageUrl" component="div" className="text-danger small mb-2" />

                                            <div className="mb-3">
                                                <label className="form-label">Title <span className="text-muted">(optional)</span></label>
                                                <Field type="text" name="title" className="form-control" placeholder="e.g. Diwali Sale Banner" />
                                            </div>

                                            <div className="mb-3">
                                                <label className="form-label">Deep Link <span className="text-danger">*</span></label>
                                                <Field type="text" name="deepLink" className="form-control" placeholder="e.g. teertham://puja/123" />
                                                <ErrorMessage name="deepLink" component="div" className="text-danger small mt-1" />
                                            </div>

                                            <div className="form-check form-switch mt-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={values.isActive}
                                                    onChange={(e) => setFieldValue("isActive", e.target.checked)}
                                                    id="isActiveSwitch"
                                                />
                                                <label className="form-check-label" htmlFor="isActiveSwitch">
                                                    {values.isActive ? "Active" : "Inactive"}
                                                </label>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
                                            <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
                                                {editing ? "Update Banner" : "Add Banner"}
                                            </button>
                                        </div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
